import React from 'react';
import { Divider } from '@mui/material';
import { useIsMobile } from '../../../hooks/useIsMobile';
import {
  AI_COLORS, AI_FONTS,
  TipBox, ImgSlot,
  PageHero, ContentWrap, SectionTitle, SubTitle,
} from '../shared';
import img49 from '../../../imgs/adulto-idosos/49.png';

export default function EnvelhecimentoBucal() {
  const isMobile = useIsMobile();

  const fases = [
    {
      fase: 'A partir dos 40 anos',
      cor: AI_COLORS.bgBlue50,
      itens: [
        'A gengiva começa a recuar levemente',
        'O esmalte dos dentes acumula desgaste',
        'O risco de doença periodontal aumenta',
        'Visitas semestrais ao dentista tornam-se ainda mais essenciais',
      ],
    },
    {
      fase: 'A partir dos 60 anos',
      cor: AI_COLORS.bgPurple50,
      itens: [
        'A produção de saliva pode diminuir',
        'O osso maxilar pode perder densidade',
        'Raízes expostas aumentam a sensibilidade e o risco de cáries radiculares',
        'O uso de múltiplos medicamentos pode impactar a saúde bucal',
      ],
    },
    {
      fase: 'A partir dos 70 anos',
      cor: AI_COLORS.teal50,
      itens: [
        'Dificuldades motoras podem comprometer a higiene bucal',
        'A articulação temporomandibular (ATM) pode apresentar problemas',
        'Próteses precisam de reajustes frequentes pois o osso muda de forma',
      ],
    },
  ];

  const estrategias = [
    { icon: '🪥', texto: 'Use escova de dentes elétrica se tiver dificuldade motora para escovar manualmente' },
    { icon: '💧', texto: 'Beba água frequentemente para controlar a boca seca' },
    { icon: '🥛', texto: 'Inclua alimentos ricos em cálcio e vitamina D para fortalecer os ossos' },
    { icon: '🚫', texto: 'Evite refrigerantes, sucos ácidos e doces em excesso' },
    { icon: '🔍', texto: 'Faça exames de boca regulares para detectar precocemente lesões suspeitas' },
    { icon: '💊', texto: 'Informe seu dentista sobre todos os medicamentos que usa' },
    { icon: '👨‍⚕️', texto: 'Consulte um especialista em odontologia geriátrica se necessário' },
  ];

  return (
    <div style={{ fontFamily: AI_FONTS.body, background: 'transparent' }}>
      <PageHero
        title="Saúde Bucal e o Envelhecimento"
        subtitle="Envelhecer não significa perder dentes. Com cuidados diários e acompanhamento profissional, é possível manter a saúde da boca em qualquer idade."
        audio="envelhecimento.m4a"
      />

      <ContentWrap>

        <p style={{ lineHeight: 1.75, marginTop: 0 }}>
          O envelhecimento traz transformações naturais à boca. Compreender essas mudanças permite
          adotar estratégias preventivas e manter qualidade de vida e saúde bucal por mais tempo.
        </p>

        <ImgSlot
          filename={img49}
          alt="Saúde bucal e o envelhecimento"
          caption="Com cuidados diários é possível manter os dentes naturais ao longo dos anos"
          maxWidth={460}
        />

        <SectionTitle>Mudanças ao longo do tempo</SectionTitle>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {fases.map((f, i) => (
            <div
              key={i}
              style={{
                background: f.cor,
                borderRadius: 14,
                padding: '20px 24px',
                borderLeft: `4px solid ${AI_COLORS.blue400}`,
              }}
            >
              <h3
                style={{
                  fontFamily: AI_FONTS.title,
                  color: AI_COLORS.blue900,
                  fontSize: '1.2rem',
                  marginTop: 0,
                  marginBottom: 12,
                }}
              >
                {f.fase}
              </h3>
              <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 2 }}>
                {f.itens.map((item, j) => (
                  <li key={j}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <Divider style={{ margin: '36px 0', borderColor: AI_COLORS.bgBlue100 }} />

        <SectionTitle>Estratégias de Prevenção</SectionTitle>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 14,
            marginBottom: 24,
          }}
        >
          {estrategias.map((e, i) => (
            <div
              key={i}
              style={{
                background: i % 2 === 0 ? AI_COLORS.bgBlue50 : AI_COLORS.bgPurple50,
                borderRadius: 12,
                padding: '16px 18px',
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
              }}
            >
              <span style={{ fontSize: '1.4rem', flexShrink: 0 }}>{e.icon}</span>
              <p style={{ margin: 0, lineHeight: 1.6, fontSize: '0.93rem' }}>{e.texto}</p>
            </div>
          ))}
        </div>

        <div
          style={{
            background: AI_COLORS.bgBlue50,
            borderRadius: 14,
            padding: '22px 26px',
            borderLeft: `4px solid ${AI_COLORS.blue400}`,
          }}
        >
          <SubTitle style={{ marginTop: 0 }}>Envelhecer com saúde bucal</SubTitle>
          <p style={{ margin: 0, lineHeight: 1.75 }}>
            Ter dentes saudáveis na terceira idade não é privilégio, é resultado de cuidados ao
            longo da vida. Nunca é tarde para começar: melhorar a higiene bucal e consultar
            regularmente um dentista traz benefícios mesmo para quem começa mais tarde.
          </p>
        </div>

        <TipBox>
          Manter uma boa saúde bucal contribui diretamente para a qualidade de vida na terceira
          idade, permitindo comer bem, sorrir com confiança e interagir socialmente sem
          constrangimento.
        </TipBox>
      </ContentWrap>
    </div>
  );
}
